import { createAction, createAsyncThunk, createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'
import { persistUser } from '@app/store/services/local-storage.service'
import {
  getProfile,
  updateProfile,
  changePassword,
  ProfileResponse,
  getProfileDetails,
  ProfileDetailsResponse,
  UpdateProfilePayload,
  ChangeProfilePasswordPayload,
  ChangeProfileResponse,
} from '@app/api/profile.api'

import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'
import { resetStore } from '@/store/actions/reset'

import { updateTemplateData } from './templates-slice'

export type ProfileUserModel = {
  id: string
}

type ProfileState = {
  data: ProfileResponse | null
  status: Status
  detailsStatus: Status
  updateStatus: Status
  passwordStatus: Status
  assignedTemplateIds: string[]
}

const initialState: ProfileState = {
  data: null,
  status: 'idle',
  detailsStatus: 'idle',
  updateStatus: 'idle',
  passwordStatus: 'idle',
  assignedTemplateIds: [],
}

export const getProfileData = createAsyncThunk<ProfileResponse, string>(
  'profile/getProfile',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getProfile(id)
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getProfileDetailsData = createAsyncThunk<ProfileDetailsResponse, string>(
  'profile/getProfileDetails',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getProfileDetails(id)
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateProfileData = createAsyncThunk<ChangeProfileResponse, UpdateProfilePayload>(
  'profile/update',
  async (updateProfilePayload, { rejectWithValue }) => {
    try {
      const res = await updateProfile(updateProfilePayload)
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const changeProfilePassword = createAsyncThunk<ChangeProfileResponse, ChangeProfilePasswordPayload>(
  'profile/changePassword',
  async (changeProfilePasswordPayload, { rejectWithValue }) => {
    try {
      const response = await changePassword(changeProfilePasswordPayload)
      return response
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const setProfileUser = createAction<PrepareAction<ProfileUserModel>>('profile/setUser', (newUser) => {
  persistUser(newUser.id)
  return {
    payload: newUser,
  }
})

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProfileData.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getProfileDetailsData.pending, (state) => {
        state.detailsStatus = 'loading'
      })
      .addCase(updateProfileData.pending, (state) => {
        state.updateStatus = 'loading'
      })
      .addCase(changeProfilePassword.pending, (state) => {
        state.passwordStatus = 'loading'
      })
      .addCase(getProfileData.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(getProfileDetailsData.rejected, (state) => {
        state.detailsStatus = 'failed'
      })
      .addCase(updateProfileData.rejected, (state) => {
        state.updateStatus = 'failed'
      })
      .addCase(changeProfilePassword.rejected, (state) => {
        state.passwordStatus = 'failed'
      })
      .addCase(getProfileData.fulfilled, (state, action: PayloadAction<ProfileResponse>) => {
        state.data = { ...state.data, ...action.payload }

        state.status = 'succeeded'
      })
      .addCase(getProfileDetailsData.fulfilled, (state, action: PayloadAction<ProfileDetailsResponse>) => {
        const payload = action.payload

        if (state.data) {
          state.data = {
            ...state.data,
            ...payload,
          }
        }
        state.detailsStatus = 'succeeded'
      })
      .addCase(updateProfileData.fulfilled, (state, action) => {
        const { data } = action.meta.arg

        if (state.data) {
          state.data = {
            ...state.data,
            ...data,
          }
        }
        state.updateStatus = 'succeeded'
      })
      .addCase(changeProfilePassword.fulfilled, (state) => {
        state.passwordStatus = 'succeeded'
      })
      .addCase(updateTemplateData.fulfilled, (state, action) => {
        const { id: updatedTemplateId, ...data } = action.meta.arg
        const profile = state.data

        updateTemplatesAssignedToProfileDevices(profile!, updatedTemplateId, data)
      })
      .addCase(resetStore, () => initialState)
  },
})

export default profileSlice.reducer

const definedTemplateFields = (data: Record<string, unknown>): Record<string, unknown> => {
  const patch: Record<string, unknown> = {}
  for (const key of [
    'deviceStatusDataType',
    'deviceStatusIndex',
    'language',
    'resetTableData',
    'resourceDataType',
    'resourceIndex',
    'resourceLabel',
  ]) {
    if (data[key] !== undefined) patch[key] = data[key]
  }
  return patch
}

const updateTemplatesAssignedToProfileDevices = (
  profile: ProfileResponse,
  updatedTemplateId: string,
  data: any,
): void => {
  const devicesTemplatesId = profile.devices.flatMap(({ deviceTemplates }) => deviceTemplates?.map(({ id }) => id))
  const firmsDevicesTemplatesId = profile.firms.flatMap((firm) =>
    firm.devices.flatMap(({ deviceTemplates }) => deviceTemplates?.map(({ id }) => id)),
  )

  const assignedTemplateIds = new Set([...devicesTemplatesId, ...firmsDevicesTemplatesId])
  const isAssigned = assignedTemplateIds.has(updatedTemplateId)

  if (!isAssigned) return

  const patch = definedTemplateFields(data)

  const applyPatch = (device: { deviceTemplates?: unknown[] | null }) => {
    device.deviceTemplates?.forEach((template) => {
      const entry = template as { id?: string }
      if (entry.id === updatedTemplateId) Object.assign(entry, patch)
    })
  }

  profile.devices.forEach(applyPatch)
  profile.firms.forEach(({ devices }) => devices.forEach(applyPatch))
}
